import { Info, Receipt } from '@mui/icons-material';
import { getFactoringCreditLineInfo } from 'lib/api';
import { downloadFilesFromRequest } from 'lib/api/files';
import DocumentSearcher from 'lib/common/DocumentSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import RequirementsDialog from 'lib/designSystem/RequirementsDialog';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { hasAnyPermissions, showAlert } from 'lib/functions/utils';
import NetworkBusinessDetails from 'pages/Network/components/Businesses/components/Details';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

import ConfirmationModule from './components/ConfirmationModule';
import CreditLineData from './components/CreditLineData';
import DocumentConfirmationDetailsModule from './components/DocumentDetail';

interface VerificationModuleProps {
  preSettedFactoringRequestId?: number;
  preSettedConfirmationType?: string;
  // eslint-disable-next-line
  setOpenShoppingCart?: (open: boolean) => void;
  // eslint-disable-next-line
  setUp?: (data?: any) => Promise<void>;
  showCreditLine?: boolean;
}

const VerificationModule: React.FC<VerificationModuleProps> = ({
  preSettedFactoringRequestId,
  preSettedConfirmationType,
  setOpenShoppingCart,
  setUp,
  showCreditLine,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<any>([]);
  const [documentsToVerify, setDocumentsToVerify] = useState<any>(null);
  const [openNewVerification, setOpenNewVerification] = useState(false);
  const [confirmationType, setConfirmationType] = useState<any>('none');
  const [factoringRequestId, setFactoringRequestId] = useState<any>(null);
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [selectedDocumentsIds, setSelectedDocumentsIds] = useState<any>([]);
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState<any>('');
  const [stakeholderNameToVerify, setStakeholderNameToVerify] =
    useState<any>('');
  const [stakeholderIdentifierToVerify, setStakeholderIdentifierToVerify] =
    useState<any>('');
  const [documentToShow, setDocumentToShow] = useState<any>(null);
  const [openDocumentDetails, setOpenDocumentDetails] = useState(false);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState<any>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [businessForDetails, setBusinessForDetails] = useState<any>(null);
  const [openBusinessDetails, setOpenBusinessDetails] = useState(false);
  const [creditLineInfo, setCreditLineInfo] = useState<any>(null);
  const [concentrations, setConcentrations] = useState<any>({
    stakeholderAssignations: [],
    subProductAssignations: [],
  });
  const [openRequirementsDialog, setOpenRequirementsDialog] = useState(false);
  const [documentId, setDocumentId] = useState<string>('');
  const [documentAlias, setDocumentAlias] = useState<string>('');

  const isPreSetted = Boolean(
    preSettedFactoringRequestId && preSettedConfirmationType,
  );

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (isPreSetted) {
      query += `confirmationType=${preSettedConfirmationType}&`;
      query += `factoringRequestId=${preSettedFactoringRequestId}&`;
      query += 'page=1&pageSize=100';
      return query;
    }
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (stakeholderIdentifier)
      query += `stakeholderIdentifier=${stakeholderIdentifier}&`;
    if (confirmationType) query += `confirmationType=${confirmationType}&`;
    if (factoringRequestId)
      query += `factoringRequestId=${factoringRequestId}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const searchDocumentsWithVerifications = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      if (!preSettedFactoringRequestId) {
        setLoading(false);
        return;
      }
      setFactoringRequestId(preSettedFactoringRequestId);

      const query = generateQuery({ page, pageSize });
      const response = await getFactoringCreditLineInfo(
        dispatch,
        preSettedFactoringRequestId,
        query,
      );

      // Extract data from consolidated response
      setCreditLineInfo(response.creditLine || null);
      setConcentrations(
        response.concentrations || {
          stakeholderAssignations: [],
          subProductAssignations: [],
        },
      );
      setDocuments(response.verifications?.data || []);
      setPagination(
        response.verifications?.pagination || {
          page: 0,
          pageSize: PAGE_SIZE,
        },
      );
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const completeSetUp = async () => {
    if (setUp) await setUp(preSettedFactoringRequestId);
    await searchDocumentsWithVerifications({ page: 0, pageSize: PAGE_SIZE });
  };

  const cantSelectRow = (row: any) => {
    if (selectedDocumentsIds.length) {
      const document = documents.find(
        (document: any) => document.id === selectedDocumentsIds[0],
      );
      if (document) {
        return document.stakeholderIdentifier !== row.stakeholderIdentifier;
      } else {
        setSelectedDocumentsIds([]);
      }
    }
    return false;
  };

  const canVerify = () => {
    if (!hasAnyPermissions(user, ['financing_verification_new'])) return false;
    return selectedDocumentsIds.length > 0;
  };

  const handleVerify = async () => {
    const selectedDocuments = documents.filter((document: any) =>
      selectedDocumentsIds.includes(document.id),
    );
    if (!selectedDocuments.length) return;

    setStakeholderNameToVerify(selectedDocuments[0].stakeholderName);
    setStakeholderIdentifierToVerify(
      selectedDocuments[0].stakeholderIdentifier,
    );

    setDocumentsToVerify(selectedDocuments);
    setOpenNewVerification(true);
  };

  const handleDetails = (id: string) => {
    const documentToShowSearch = documents.find((doc: any) => doc.id === id);
    if (!documentToShowSearch) return;
    setDocumentToShow(documentToShowSearch);
    setOpenDocumentDetails(true);
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
    },
  ];

  const handleDownloadDocumentPDF = async (documentId: string) => {
    const document = documents.find((x: any) => x.id === documentId);
    const documentKey = `${document.businessIdentifier}-${document.folio}`;
    setLoading(true);
    try {
      const data = {
        requestId: documentKey,
        fileName: `${documentKey}.pdf`,
        requestType: 'document',
      };
      const response = await downloadFilesFromRequest(dispatch, data);
      if (response) {
        window.open(response);
      } else {
        showAlert(dispatch, 'error', 'No hay archivo para descargar');
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleOpenRequirementsDialog = (rowId: string) => {
    const document = documents.find(
      (x: (typeof documents)[0]) => x.id === rowId,
    );
    if (document) {
      setDocumentId(document.id);
      setDocumentAlias(`Folio ${document.folio}`);
      setOpenRequirementsDialog(true);
    }
  };

  const handleDownloadDocumentPDFObject = {
    key: 'downloadDocumentPDF',
    label: '',
    handleAction: handleDownloadDocumentPDF,
    icon: <Receipt />,
    keyDisabled: 'fileStatus',
  };

  const handleRequirements = {
    key: 'requirements',
    label: '',
    handleAction: handleOpenRequirementsDialog,
    type: 'requirements',
  };

  const handleShowBusinessDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  const handleShowStakeholderDetails = (id: string) => {
    const row: any = documents.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.stakeholderIdentifier,
        name: row.stakeholderName,
      }),
    );
  };

  useEffect(() => {
    void searchDocumentsWithVerifications({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div style={{ paddingBottom: '20px' }}>
      {showCreditLine && (
        <CreditLineData
          creditLineInfo={creditLineInfo}
          concentrations={concentrations}
        />
      )}
      <HeaderWithActions
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            hidden: isPreSetted,
            customButton: (
              <DocumentSearcher
                searchData={searchDocumentsWithVerifications}
                setData={setDocuments}
                setConfirmationType={setConfirmationType}
                confirmationType={confirmationType}
                setLoading={setLoading}
                startDate={startDate}
                endDate={endDate}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                stakeholderIdentifier={stakeholderIdentifier}
                setStakeholderIdentifier={setStakeholderIdentifier}
                pagination={pagination}
                factoringRequestId={factoringRequestId}
                setFactoringRequestId={setFactoringRequestId}
              />
            ),
          },
          {
            name: 'Verificar',
            action: handleVerify,
            color: 'primary',
            disabled: !canVerify(),
          },
        ]}
        title="Aquí verás los documentos junto a sus verificaciones."
        variant="body1"
      />
      <div style={{ marginBottom: '16px' }}>
        {documents.length > 0 ? (
          <TableWithPagination
            tableSize="small"
            exportToFile="xlsx"
            dataHeaders={[
              ...(!isPreSetted
                ? [{ key: 'factoringRequestId', label: 'Solicitud' }]
                : []),
              // handleDownloadDocumentPDFObject,
              handleRequirements,
              { key: 'folio', label: 'Folio' },
              {
                key: 'businessName',
                label: 'Emisor',
                handleAction: handleShowBusinessDetails,
                icon: <Info fontSize="small" />,
              },
              {
                key: 'stakeholderName',
                label: 'Receptor',
                handleAction: handleShowStakeholderDetails,
                icon: <Info fontSize="small" />,
              },
              { key: 'stakeholderIdentifier', label: 'Rut Receptor' },
              { key: 'amount', label: 'Monto', type: 'money' },
              { key: 'confirmDate', label: 'Verificación', type: 'date' },
              {
                key: 'confirmStatus',
                label: 'Status',
                type: 'verificationStatus',
                canNull: true,
              },
              { key: 'documentConfirmation', label: 'Tipo' },
              { key: 'issuedDate', label: 'Emisión', type: 'date' },
              {
                key: 'receiptDate',
                label: 'Recepción',
                type: 'eight-days-date',
              },
              {
                key: 'confirmDateSII',
                label: 'Confirm. SII',
                type: 'confirm-lights',
                canNull: true,
              },
              {
                key: 'cancelDateSII',
                label: 'Rechazo SII',
                type: 'cancel-lights',
                canNull: true,
              },
              {
                key: 'creditNotesDate',
                label: 'NC ',
                type: 'cancel-lights-with-credit-note-amount',
                canNull: true,
              },
              {
                key: 'cedded',
                label: 'Cedida',
                type: 'cession-lights',
                canNull: true,
              },
            ]}
            data={documents}
            loading={loading}
            selected={selectedDocumentsIds}
            setSelected={setSelectedDocumentsIds}
            setData={setDocuments}
            searchData={searchDocumentsWithVerifications}
            setPagination={setPagination}
            pagination={pagination}
            cantSelectRow={cantSelectRow}
            actions={actions}
          />
        ) : (
          <div
            style={{
              padding: '20px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #dee2e6',
            }}
          >
            <div
              style={{
                fontSize: '16px',
                color: '#6c757d',
                marginBottom: '8px',
              }}
            >
              No se encontraron documentos
            </div>
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              No hay documentos disponibles para mostrar con los filtros
              actuales.
            </div>
          </div>
        )}
      </div>
      {setOpenShoppingCart && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '16px',
            marginBottom: '8px',
          }}
        >
          <CustomButton
            color="secondary"
            onClick={() => setOpenShoppingCart(false)}
          >
            Salir
          </CustomButton>
        </div>
      )}
      {openNewVerification && (
        <ConfirmationModule
          documents={documentsToVerify}
          stakeholderName={stakeholderNameToVerify}
          stakeholderIdentifier={stakeholderIdentifierToVerify}
          open={openNewVerification}
          setOpen={setOpenNewVerification}
          setUp={completeSetUp}
          pagination={pagination}
        />
      )}
      {openDocumentDetails && (
        <DocumentConfirmationDetailsModule
          document={documentToShow}
          open={openDocumentDetails}
          setOpen={setOpenDocumentDetails}
        />
      )}
      {businessForDetails && (
        <NetworkBusinessDetails
          open={openBusinessDetails}
          setOpen={setOpenBusinessDetails}
          business={businessForDetails}
        />
      )}
      {openRequirementsDialog && (
        <RequirementsDialog
          open={openRequirementsDialog}
          onClose={() => setOpenRequirementsDialog(false)}
          category={'factoringDocument'}
          entityKey={'documentId'}
          entityId={documentId}
          entityAlias={documentAlias}
        />
      )}
    </div>
  );
};

export default VerificationModule;
